import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Box,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { VenueDocument } from "../types/venues.types";
import { useState } from "react";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import { Close } from "@mui/icons-material";

interface VenueCoverDialogProps {
  open: boolean;
  onClose: () => void;
  images: VenueDocument[];
  currentCoverUrl?: string;
  onCoverUpdated?: (newCoverUrl: string) => void;
}

export const VenueCoverDialog: React.FC<VenueCoverDialogProps> = ({
  open,
  onClose,
  images,
  currentCoverUrl,
  onCoverUpdated,
}: VenueCoverDialogProps) => {
  const [selectedPhoto, setSelectedPhoto] = useState<VenueDocument | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleUseAsCoverChange = async () => {
    if (!selectedPhoto) {
      toast.error("Please select an image first");
      return;
    }

    try {
      setIsLoading(true); // start loading
      await apiService.post(`/api/venues/document/change-cover/`, {
        id: selectedPhoto.id,
      });

      toast.success("Cover photo updated successfully");
      onCoverUpdated?.(selectedPhoto.file);
      onClose();
    } catch (err) {
      let errorMessage = "Failed to update cover photo.";
      const errorData = (
        err as { response?: { data?: { detail?: string; message?: string } } }
      )?.response?.data;

      if (errorData?.detail) errorMessage = errorData.detail;
      if (errorData?.message) errorMessage = errorData.message;

      toast.error(errorMessage);
    } finally {
      setIsLoading(false); // stop loading
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle>Select Cover Photo</DialogTitle>
        <IconButton sx={{ mr: 2 }} onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent>
        <Grid container spacing={2}>
          {images.map((img) => {
            const isCurrent = img.file === currentCoverUrl;
            const isSelected = selectedPhoto?.id === img.id;

            return (
              <Grid size={{ xs: 6, sm: 4, md: 4 }} key={img.id}>
                <Box
                  onClick={() => setSelectedPhoto(img)}
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: "pointer",
                    border: isCurrent
                      ? "3px solid #4caf50"
                      : isSelected
                        ? "3px solid #C29363"
                        : "2px solid transparent",
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "scale(1.03)",
                    },
                  }}
                >
                  <img
                    src={img.file}
                    alt="Venue"
                    style={{
                      width: "100%",
                      height: 120,
                      objectFit: "cover",
                    }}
                  />

                  {isCurrent && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 6,
                        left: 6,
                        backgroundColor: "#4caf50",
                        color: "white",
                        px: 1,
                        borderRadius: 1,
                        fontSize: "0.7rem",
                      }}
                    >
                      Current
                    </Box>
                  )}

                  {isSelected && (
                    <CheckCircleIcon
                      sx={{
                        position: "absolute",
                        bottom: 6,
                        right: 6,
                        color: "#C29363",
                      }}
                    />
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Action */}
        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button
            variant="contained"
            disabled={!selectedPhoto || isLoading}
            onClick={handleUseAsCoverChange}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isLoading ? "Updating..." : "Use as cover"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
